import { useMemo } from "react";
import type {
  BaseEntity,
  EntityHooks,
  SerializedViewState,
  SimpleTableConfig,
} from "../../../UniversalCRMTable/types";
import type {
  BulkAction,
  BulkActionWithCallback,
  BulkActionWithOnClick,
} from "../../../BulkActions/types";
import type { ConfigWithEnhancedBulkActions } from "@/lib/types/bulk-actions-enhanced";
import type { BulkActions } from "../../contexts/types";
import { useBulkActionsComposer } from "../useBulkActionsComposer";
import { GenericBulkEditModal } from "../../../BulkActions/GenericBulkEditModal";
import { getBulkEditableFields } from "../../../BulkActions/utils/fieldBasedBulkActions";

interface BulkActionsCompositionResult<T extends BaseEntity> {
  bulkActions: BulkActions;
  configWithEnhancedBulkActions: ConfigWithEnhancedBulkActions<T>;
}

function hasOnClick<T extends BaseEntity>(
  action: BulkAction<T> | BulkActionWithCallback<T>
): action is BulkActionWithOnClick<T> {
  return "onClick" in action;
}

/**
 * Handles bulk actions composition and config enhancement
 * Adds onClick handlers to bulk actions and injects GenericBulkEditModal
 */
export function useCompositionBulkActions<T extends BaseEntity>(
  entityName: string,
  normalizedCurrentFilters: SerializedViewState,
  bulkActionMutations: { emailExport?: unknown } | undefined,
  setBulkEmailExportData: (data: { emailList: string; totalCount: number } | null) => void,
  config: SimpleTableConfig<T>,
  hooks: EntityHooks<T>
): BulkActionsCompositionResult<T> {
  const bulkActions = useBulkActionsComposer(
    entityName,
    normalizedCurrentFilters,
    bulkActionMutations,
    setBulkEmailExportData
  );

  // Enhance config bulk actions with handlers
  // Add onClick handlers to all bulk actions
  // CRITICAL: This must be created BEFORE callbacks so renderSimpleTable uses the enhanced config
  const configWithEnhancedBulkActions: ConfigWithEnhancedBulkActions<T> = useMemo(() => {
    const sourceActions = (config.bulkActions ?? []) as Array<
      BulkAction<T> | BulkActionWithCallback<T>
    >;
    const enhancedBulkActions: BulkActionWithOnClick<T>[] = sourceActions.map((action) => {
      if (hasOnClick(action)) {
        return action;
      }

      // Add specific handlers for known actions
      if (action.id === "bulk_export_emails" || action.id === "bulk_email") {
        return {
          ...action,
          onClick: bulkActions.handleBulkExportEmails,
        };
      }

      // For other bulk actions without specific handlers, provide a generic handler
      // that calls onBulkAction with the action ID
      return {
        ...action,
        onClick: async (_selectedItems: T[], _selectedIds: string[]) => {
          // This will be handled by the table's onBulkAction callback
          // which dispatches to the appropriate handler based on action ID
          console.warn(`No specific handler for bulk action: ${action.id}. Using generic handler.`);
        },
      };
    });

    // Inject GenericBulkEditModal when config has no editAllFieldsModal but has bulk editable fields
    const bulkEditableCount = getBulkEditableFields(config).length;
    const hasUseUpdate = Boolean(hooks?.useUpdate);
    const editAllFieldsModal =
      config.editAllFieldsModal ??
      (bulkEditableCount > 0 && hasUseUpdate ? GenericBulkEditModal : undefined);

    return {
      ...config,
      bulkActions: enhancedBulkActions,
      ...(editAllFieldsModal && { editAllFieldsModal }),
    };
  }, [config, bulkActions.handleBulkExportEmails, hooks?.useUpdate]);

  return {
    bulkActions,
    configWithEnhancedBulkActions,
  };
}
